import { FmeaItem, EditableFmeaRow, IterationRecord } from '../types';
import { normalizeProjectName, extractProjectName } from './validation';

export type FilterableColumn = 'id' | 'function' | 'itemFunction' | 'specialCharacteristic';

export interface FmeaFilterOptions {
    searchTerm: string;
    columnFilters: Record<FilterableColumn, Set<string>>;
    selectedEpochFilter: string;
    activeProject: string;
    viewMode: 'active' | 'draft';
}

const isSearchMatch = (item: EditableFmeaRow, searchTerm: string) => {
    return searchTerm === '' || (item.id || '').toLowerCase().includes(searchTerm.toLowerCase());
};

const isColumnMatch = (item: EditableFmeaRow, filters: Record<FilterableColumn, Set<string>>) => {
    if (filters.id.size > 0 && !filters.id.has(item.id)) return false;
    if (filters.function.size > 0 && !filters.function.has(item.function)) return false;
    if (filters.itemFunction.size > 0 && !filters.itemFunction.has(item.itemFunction)) return false;

    const scFilterSet = filters.specialCharacteristic;
    if (scFilterSet && scFilterSet.size > 0) {
        if (!scFilterSet.has(item.specialCharacteristic || 'none')) return false;
    }
    return true;
};

const isProjectMatch = (item: EditableFmeaRow, activeProject: string, history: IterationRecord[]) => {
    if (activeProject === 'all') return true;

    const activeProjectNormalized = normalizeProjectName(activeProject);
    if (item.projectName) {
        return normalizeProjectName(item.projectName) === activeProjectNormalized;
    }

    const itemEpoch = history.find(e => (e.epochNumber || e.iteration) === item.epochId);
    if (itemEpoch?.epochName) {
        return normalizeProjectName(extractProjectName(itemEpoch.epochName)) === activeProjectNormalized;
    }
    return false;
};

export const filterFmeaItems = (
    data: EditableFmeaRow[],
    options: FmeaFilterOptions,
    iterationHistory: IterationRecord[] = []
): EditableFmeaRow[] => {
    const { searchTerm, columnFilters, selectedEpochFilter, activeProject, viewMode } = options;

    return data.filter(item => {
        if (!isSearchMatch(item, searchTerm)) return false;
        if (!isColumnMatch(item, columnFilters)) return false;

        const epochMatch = selectedEpochFilter === 'all' || String(item.epochId) === selectedEpochFilter;
        if (!epochMatch) return false;

        if (!isProjectMatch(item, activeProject, iterationHistory)) return false;

        const itemStatus = item.status || 'draft';
        return viewMode === 'active' ? itemStatus === 'active' : itemStatus === 'draft';
    });
};

export interface SortConfig {
    key: string | null;
    direction: 'asc' | 'desc' | null;
}

const getSortValue = (item: Record<string, any>, key: string): string | number | null | undefined => {
    if (key === 'rpn') {
        return (Number(item.S) || 0) * (Number(item.O) || 0) * (Number(item.D) || 0);
    }
    if (key === 'sxo') {
        return (Number(item.S) || 0) * (Number(item.O) || 0);
    }
    if (key === 'sxd') {
        return (Number(item.S) || 0) * (Number(item.D) || 0);
    }
    const val = item[key];
    if (['S', 'O', 'D'].includes(key)) {
        return Number(val);
    }
    return val;
};

export const sortFmeaItems = (
    data: EditableFmeaRow[],
    sortConfig: SortConfig
): EditableFmeaRow[] => {
    if (!sortConfig.key || !sortConfig.direction) return data;

    return [...data].sort((a, b) => {
        const valA = getSortValue(a as any, sortConfig.key!);
        const valB = getSortValue(b as any, sortConfig.key!);

        if (valA === valB) return 0;
        if (valA === undefined || valA === null) return 1;
        if (valB === undefined || valB === null) return -1;

        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        return sortConfig.direction === 'asc' ? 1 : -1;
    });
};
